import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Spade, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils/general";
import { Skeleton } from "@/components/ui/skeleton";
import { getThemeCardTypes } from "@/services/api";
import { useToast } from "@/hooks/useToast";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface ThemeCardTypesProps {
  themeId: string;
  onBack?: () => void;
  onContinue?: () => void;
  onSendToShelter?: () => void;
}

interface CardType {
  name: string;
  description: string;
}

const CARD_TYPE_ICONS = [
  { color: "text-blue-pill" },    // First card type always blue
  { color: "text-red-pill" },     // Second card type always red
  { color: "text-theme-one" },    // Third card type teal
  { color: "text-theme-three" }   // Fourth card type orange
] as const;

export function ThemeCardTypes({ themeId, onBack, onContinue }: ThemeCardTypesProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [cardTypes, setCardTypes] = useState<CardType[]>([]);
  const [openItems, setOpenItems] = useState<number[]>([]);

  useEffect(() => {
    const fetchCardTypes = async () => {
      try {
        const response = await getThemeCardTypes(themeId);
        if (response.success) {
          setCardTypes(response.data.cardTypes);
        } else {
          throw new Error(response.error || 'Failed to fetch card types');
        }
      } catch (error) {
        console.error('Error fetching card types:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load card types. Please try again."
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCardTypes();
  }, [themeId]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <Card className="w-full animate-slideUp border-border bg-card shadow-lg flex flex-col h-[calc(100dvh-10rem)]">
      <CardContent className="p-6 flex flex-col h-full">
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Kinds of Cards
            </h2>
            <p className="text-muted-foreground">
              Your game's core mechanics
            </p>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 space-y-4">
            {isLoading ? (
              // Loading skeletons
              Array(4).fill(null).map((_, i) => (
                <div key={i} className="p-4 border rounded-lg border-border">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-6 w-1/3" />
                  </div>
                </div>
              ))
            ) : (
              // Card type display with collapsible
              cardTypes.map((type, index) => (
                <Collapsible
                  key={index}
                  open={openItems.includes(index)}
                  onOpenChange={() => toggleItem(index)}
                  className="border rounded-lg border-border bg-card/50 transition-all duration-200 hover:border-game-accent/50"
                >
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <Spade className={cn(
                          "h-5 w-5",
                          CARD_TYPE_ICONS[index]?.color || "text-muted-foreground"
                        )} />
                        <h3 className="font-semibold text-foreground">{type.name}</h3>
                      </div>
                      <ChevronDown 
                        className={cn(
                          "h-4 w-4 text-muted-foreground transition-transform duration-200",
                          openItems.includes(index) ? "rotate-180" : ""
                        )}
                      />
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-4 pb-4 pt-0">
                      <div className="pl-8">
                        <p className="text-sm text-muted-foreground">{type.description}</p>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))
            )}
          </div>

          {/* Fixed Actions */}
          <p className="text-muted-foreground">
            These base mechanics can be flavored by your chosen art style.
          </p>
          <div className="flex justify-between items-center pt-6 mt-8 border-t border-border">
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-muted-foreground"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={onContinue}
              disabled={isLoading}
              className="text-background bg-logo hover:bg-logo/90"
            >
              Create
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 